// Remembered coordination project (2026-08-18).
//
// Every coordination page with a project picker (issues list, create /
// batch issue, checklists, participants, plan builder, timeline, analytics,
// approval queue, dashboard...) shares ONE remembered selection: pick a
// project anywhere and the next page - and the next login on the same
// browser - opens on it. Stored per environment (prefix) and per user id so
// two accounts on one machine don't leak into each other.
//
// 2026-09-18 (owner: "by user"): the server keeps it too (user preference
// 'lastProject', helpers/userPrefs), so it follows the person to another
// browser or device; this browser's key is only the cache.
//
//   const remembered = getRememberedProject(userId);     // id | ""
//   rememberProject(userId, projectId);                  // "" / null clears
//   useRememberedProject(userId, projects, [preferredId]) // hook, see below
import { useEffect, useMemo, useState } from "react";
import { tokenRef } from "api";
import { currentUserId, getPref, loadPrefs, setPref } from "./userPrefs";

const KEY_BASE = `${String(tokenRef).replace(/_token$/, "")}_coordination_project`;
const keyFor = (userId) => `${KEY_BASE}:${userId || "anon"}`;

// Pre-2026-08-18 the coordination pages kept a single (per-env) key; adopt
// its value once for whoever logs in first so nobody loses their project.
const LEGACY_KEY = `${KEY_BASE.replace(/_coordination_project$/, "")}_last_coordination_project`;

export function getRememberedProject(userId) {
  const server = getPref(userId, "lastProject");
  if (server) return String(server);
  try {
    const v = localStorage.getItem(keyFor(userId));
    if (v) return v;
    const legacy = localStorage.getItem(LEGACY_KEY);
    if (legacy) { localStorage.setItem(keyFor(userId), legacy); localStorage.removeItem(LEGACY_KEY); return legacy; }
    return "";
  } catch (e) { return ""; }
}

export function rememberProject(userId, projectId) {
  try {
    if (projectId) localStorage.setItem(keyFor(userId), String(projectId));
    else localStorage.removeItem(keyFor(userId));
  } catch (e) { /* storage unavailable */ }
  if (userId && String(getPref(userId, "lastProject") || "") !== String(projectId || "")) setPref(userId, "lastProject", projectId ? String(projectId) : null);
  try { window.dispatchEvent(new CustomEvent("bl:project-remembered", { detail: { userId, projectId: projectId || "" } })); } catch (e) { /* ignore */ }
}

// Resolves the project a page should start on:
//   1. `preferredId` (route param / ?projectId= query) wins and is remembered,
//   2. else the remembered one - if it is still in `projects` (when a list is given),
//   3. else "" (caller falls back to its own default, e.g. first project).
// Returns [projectId, setProjectId]; setProjectId remembers as it sets.
export function useRememberedProject(userId, projects, preferredId) {
  const listIds = useMemo(() => (Array.isArray(projects) ? projects.map((p) => String(p?._id || p?.value || p)) : null), [projects]);
  const [projectId, setProjectIdState] = useState(() => preferredId || getRememberedProject(userId));
  // Route/query preference (deep link) - follow it and remember it, on
  // first mount too.
  useEffect(() => {
    if (!preferredId) return;
    if (preferredId !== projectId) setProjectIdState(preferredId);
    rememberProject(userId, preferredId);
  }, [preferredId]); // eslint-disable-line
  // The server's copy (another device may have moved it on): follow it
  // unless the page was opened on a project; seed it from this browser once.
  useEffect(() => {
    if (!userId) return undefined;
    let alive = true;
    loadPrefs(userId).then((body) => {
      if (!alive || !body || preferredId) return; // opened on a project: that one wins and is being saved
      const server = String((body.values || {}).lastProject || "");
      if (!server) { const local = getRememberedProject(userId); if (local) setPref(userId, "lastProject", local); return; }
      if (server !== String(projectId || "")) { rememberProject(userId, server); setProjectIdState(server); }
    });
    return () => { alive = false; };
  }, [userId]); // eslint-disable-line
  // Once the list is known, drop a remembered id that is no longer available.
  useEffect(() => {
    if (!listIds || !projectId || preferredId) return;
    if (!listIds.includes(String(projectId))) setProjectIdState("");
  }, [listIds]); // eslint-disable-line
  const setProjectId = (id) => { setProjectIdState(id || ""); rememberProject(userId, id || ""); };
  return [projectId, setProjectId];
}

// A page opened on a project (route param) makes it the person's last
// project too, so every other tab follows (owner, 2026-09-18).
export function useRememberRouteProject(projectId) {
  useEffect(() => {
    const uid = currentUserId();
    if (projectId && uid) rememberProject(uid, String(projectId));
  }, [projectId]);
}
